// ============================================================================
// PROTECTED ROUTE — Route-level authentication + module access guard
// ============================================================================
// Wraps routes that require authentication and optionally a specific module.

import React, { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { getAuthMode } from '../../auth/modeHelpers';
import { hasActiveSession } from '../../auth/sessionState';

interface ProtectedRouteProps {
  children: ReactNode;
  /** Require access to a specific module */
  moduleCode?: string;
  /** Redirect target when not authenticated (default: '/') */
  redirectTo?: string;
  /** Loading component to show while checking auth */
  loadingComponent?: ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  moduleCode,
  redirectTo = '/',
  loadingComponent,
}) => {
  const { isAuthenticated, isLoading, permissionEngine } = useAuthContext();

  // Legacy mode: AuthContext's isAuthenticated always returns true, so we must
  // inspect the same signals LoginInterface writes. Without this guard, browser
  // Back after logout re-enters the app from history.
  if (getAuthMode() !== 'tenant-hub') {
    if (!hasActiveSession()) {
      return <Navigate to={redirectTo} replace />;
    }
    return <>{children}</>;
  }

  if (isLoading) {
    return <>{loadingComponent ?? <div>Loading...</div>}</>;
  }

  // Belt-and-braces: AuthContext state AND the persisted session must agree.
  // After a cross-tab logout wipes storage, context state may still be stale in
  // this tab for a tick — `hasActiveSession()` catches that.
  if (!isAuthenticated || !hasActiveSession()) {
    return <Navigate to={redirectTo} replace />;
  }

  if (moduleCode && !permissionEngine.hasModuleAccess(moduleCode)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
